import type { MouseEvent, ReactNode } from "react";
import { createElement, Fragment } from "react";
import type { ConceptBrief } from "../concept/ConceptType";

function sortConceptsByNameLengthDesc(concepts: ConceptBrief[]): ConceptBrief[] {
  return [...concepts]
    .filter((c) => c.name?.trim() && c.code?.trim())
    .sort((a, b) => b.name.trim().length - a.name.trim().length);
}

function matchConceptAt(
  text: string,
  index: number,
  sorted: ConceptBrief[]
): { concept: ConceptBrief; label: string; length: number } | null {
  const prefix = text[index] === "#" ? "#" : "";
  const start = prefix ? index + 1 : index;
  for (const c of sorted) {
    const nm = c.name.trim();
    if (nm && text.startsWith(nm, start)) {
      return { concept: c, label: `${prefix}${nm}`, length: prefix.length + nm.length };
    }
  }
  return null;
}

/** 按正文首次出现顺序提取提及的概念（长名优先匹配） */
export function extractMentionedConcepts(
  text: string | null | undefined,
  catalog: ConceptBrief[]
): ConceptBrief[] {
  const src = String(text ?? "");
  if (!src.trim() || catalog.length === 0) {
    return [];
  }
  const sorted = sortConceptsByNameLengthDesc(catalog);
  const seen = new Set<string>();
  const result: ConceptBrief[] = [];
  for (let i = 0; i < src.length; ) {
    const matched = matchConceptAt(src, i, sorted);
    if (matched) {
      if (!seen.has(matched.concept.code)) {
        seen.add(matched.concept.code);
        result.push(matched.concept);
      }
      i += matched.length;
    } else {
      i += 1;
    }
  }
  return result;
}

/** 将正文中匹配到的概念名渲染为可点击蓝色链接 */
export function renderViewpointConceptLinkedText(
  text: string | null | undefined,
  catalog: ConceptBrief[],
  onConceptClick: (concept: ConceptBrief) => void
): ReactNode {
  const src = String(text ?? "");
  if (!src || catalog.length === 0) {
    return src;
  }
  const sorted = sortConceptsByNameLengthDesc(catalog);
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (let i = 0; i < src.length; ) {
    const matched = matchConceptAt(src, i, sorted);
    if (matched) {
      if (i > lastIndex) {
        parts.push(src.slice(lastIndex, i));
      }
      parts.push(
        createElement(
          "button",
          {
            key: `vp-concept-${key++}`,
            type: "button",
            className: "lf-vp-concept-link",
            onClick: (e: MouseEvent) => {
              e.stopPropagation();
              onConceptClick(matched.concept);
            },
          },
          matched.label
        )
      );
      i += matched.length;
      lastIndex = i;
    } else {
      i += 1;
    }
  }

  if (lastIndex < src.length) {
    parts.push(src.slice(lastIndex));
  }
  return parts.length > 0 ? createElement(Fragment, null, ...parts) : src;
}
